import React, { Component } from "react";
import { PageLayout } from "../../components/ui";
import _ from "lodash";
import { Column } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import { KycSubmission } from "../../models/KycSubmission";
import { Button } from "primereact/button";
import { confirmDanger } from "../../utils/confirmHelpers";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { dateFilterTemplate, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import KycIndividualModal from "./KycIndividualModal";
import { utils } from "../../utils/utils";
import { fileService } from "../../services/FileService";
import saveAs from "file-saver";
import JSZip from "jszip";
import { Download } from "lucide-react";
import { toastService } from "../../services/toastService";

interface PendingKycSubmissonsState {
    isLoading: boolean;
    kycSubmission: KycSubmission[];
    viewKycSubmissionModal: boolean;
    selectedSubmission: KycSubmission | null;
    totalRecords: number;
}

export default class PendingKycSubmissons extends Component<unknown, PendingKycSubmissonsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: unknown) {
        super(props);

        this.state = {
            isLoading: false,
            kycSubmission: [],
            viewKycSubmissionModal: false,
            selectedSubmission: null,
            totalRecords: 0,
        };
    }

    loadSubmissionPendingReviewData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        const submissionResult = await kycSubmissionApiService.getAllPendingReview(searchRequest);

        this.setState({ isLoading: false });

        if (!submissionResult.success) {
            toastService.error(submissionResult.message);
            return;
        }

        this.setState({ kycSubmission: submissionResult.data, totalRecords: submissionResult.totalCount });
    };

    onActionButtonClick = (rowData: KycSubmission) => {
        confirmDanger(
            "This action will start the review and is irreversible. Are you sure you want to proceed?",
            "Start Review",
            () => this.startReviewing(rowData),
        );
    };
    downloadDocuments = async (submissionId: number) => {
        this.setState({ isLoading: true });

        const documentsResult = await kycSubmissionApiService.getDocuments(submissionId);

        if (!documentsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(documentsResult.message);
            return;
        }

        const zip = new JSZip();
        const photoZip = zip.folder("documents");

        const data = documentsResult.data;
        for (let i = 0; i < data.length; i++) {
            const result = data[i];
            await this.onDownloadFromFileStorage(result.fileStorageId, result.fileName, photoZip);
        }
        /// waits for "await DownloadFileFromS3, then executes the save as which saves the zipped folder created in "DownloadFileFromS3"
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documents");

        this.setState({ isLoading: false });
    };
    onDownloadFromFileStorage = async (fileStorageId: string, secondaryLabel: string, photoZip: any): Promise<void> => {
        const imagePath = await fileService.GetFile(fileStorageId);

        if (!imagePath.success) {
            this.setState({ isLoading: false });
            toastService.error(imagePath.message);
            return;
        }

        photoZip.file(secondaryLabel, imagePath.data, { base64: true });
    };
    startReviewing = async (row: KycSubmission) => {
        const result = await kycSubmissionApiService.transitionToUnderReview(row.id);

        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({ viewKycSubmissionModal: true, selectedSubmission: row });
    };

    actionBodyTemplate = (row: KycSubmission) => {
        return <Button label="Review" type="button" severity="warning" onClick={() => this.onActionButtonClick(row)}></Button>;
    };

    toggleKycSubmissionModalView = () => {
        this.setState({ viewKycSubmissionModal: false });
        this.loadSubmissionPendingReviewData({
            pageNumber: this.dataTable?.state.page!,
            pageSize: this.dataTable?.state.pageRowCount!,
            filters: [],
            sortings: [],
        });
    };

    render() {
        return (
            <PageLayout noTitle>
                            {
                                this.state.viewKycSubmissionModal && (
                                    <KycIndividualModal onHide={this.toggleKycSubmissionModalView} submission={this.state.selectedSubmission!} />
                                )
                                // <KycSubmissionModal
                                //     onHide={this.toggleKycSubmissionModalView}
                                //     submission={this.state.selectedSubmission!}
                                // />
                            }
                                <ExtendedDataTable
                                    value={this.state.kycSubmission}
                                    totalRecords={this.state.totalRecords}
                                    onChange={this.loadSubmissionPendingReviewData}
                                    loading={this.state.isLoading}
                                    ref={(ref) => (this.dataTable = ref)}
                                    defaultSortOrder={SortOrderType.Descending}
                                    defaultSortField="submissionTimeStamp"
                                >
                                    <Column {...({ alwaysVisible: true } as any)} field="id" header="Submission ID" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                                    <Column
                                        header="End User First Name"
                                        field="endUserFirstName"
                                        {...({ alwaysVisible: true } as any)}
                                        body={(rowData: KycSubmission) => <span>{rowData.endUserFirstName}</span>}
                                        filterMatchModeOptions={stringFilterMatchModeOptions}
                                    ></Column>
                                    <Column
                                        field="merchantName"
                                        header="Merchant Name"
                                        sortable
                                        {...({ alwaysVisible: true } as any)}
                                        filterMatchModeOptions={stringFilterMatchModeOptions}
                                    ></Column>
                                    <Column
                                        header="End User Last Name"
                                        field="endUserLastName"
                                        {...({ alwaysVisible: true } as any)}
                                        body={(rowData: KycSubmission) => <span>{rowData.endUserLastName}</span>}
                                        filterMatchModeOptions={stringFilterMatchModeOptions}
                                    ></Column>
                                    <Column
                                        field="endUserPhone"
                                        header="End User Phone"
                                        sortable
                                        filterMatchModeOptions={stringFilterMatchModeOptions}
                                    ></Column>
                                    <Column
                                        field="endUserEmail"
                                        header="End User Email"
                                        sortable
                                        filterMatchModeOptions={stringFilterMatchModeOptions}
                                    ></Column>
                                    <Column field="reason" header="Risk reason" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                                    <Column
                                        field="submissionTimeStamp"
                                        header="Submission TimeStamp"
                                        body={dateBodyTemplate("submissionTimeStamp")}
                                        filterMatchModeOptions={numberFilterMatchModeOptions}
                                        dataType="date"
                                        filterElement={dateFilterTemplate}
                                    />
                                    <Column
                                        body={(rowData: KycSubmission) => (
                                            <button
                                                type="button"
                                                disabled={rowData.documentsCount === 0}
                                                onClick={(e) => { e.stopPropagation(); this.downloadDocuments(rowData.id); }}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                                title="Download documents"
                                            >
                                                <Download size={14} />
                                                <span>Download</span>
                                            </button>
                                        )}
                                        header="Uploaded Files"
                                    ></Column>
                                    <Column
                                        header="Action"
                                        body={this.actionBodyTemplate}
                                        headerStyle={{ width: "8em", textAlign: "center" }}
                                        bodyStyle={{ textAlign: "center", overflow: "visible" }}
                                    />
                                </ExtendedDataTable>
            </PageLayout>
        );
    }
}
